import React from "react";
import { Bell, ChevronDown, UserCircle } from "lucide-react";
import { Logo } from "./Logo";
import type { NavItem, RoleDef, TickerItem } from "./types";

export interface AppShellProps {
  /** Small uppercase line under the logo, e.g. "Wafer Intelligence · DAO" */
  brandSubtitle?: string;
  ticker?: TickerItem[];
  showDaoBadge?: boolean;
  /** If provided, renders a horizontally-scrollable role switcher. */
  roles?: RoleDef[];
  activeRoleId?: string;
  onRoleChange?: (id: string) => void;
  /** Nav items for the current role/section — rendered as sidebar (desktop) + bottom tabs (mobile). */
  navItems: NavItem[];
  activeNavId: string;
  onNavChange: (id: string) => void;
  /** Extra controls rendered on the right of the header (e.g. environment switcher). */
  headerExtra?: React.ReactNode;
  children: React.ReactNode;
}

const tickerToneClass: Record<NonNullable<TickerItem["direction"]>, string> = {
  up: "netiv-up",
  down: "netiv-down",
  warn: "netiv-warn",
  neutral: "",
};

export function AppShell({
  brandSubtitle,
  ticker,
  showDaoBadge,
  roles,
  activeRoleId,
  onRoleChange,
  navItems,
  activeNavId,
  onNavChange,
  headerExtra,
  children,
}: AppShellProps) {
  const activeGroup = navItems.find((item) =>
    item.children?.some((child) => child.id === activeNavId),
  );

  return (
    <div className="netiv-shell">
      <header className="netiv-shell__header">
        <div className="netiv-shell__header-row">
          <div className="netiv-shell__brand">
            <Logo height={24} />
            {brandSubtitle && <span className="netiv-shell__brand-sub">{brandSubtitle}</span>}
          </div>
          <div className="netiv-shell__header-right">
            {headerExtra}
            {showDaoBadge && (
              <span className="netiv-shell__dao-badge">
                <span className="netiv-shell__dao-dot" />
                DAO LIVE
              </span>
            )}
            <span className="netiv-shell__icon-btn" aria-label="Notifications">
              <Bell size={15} />
            </span>
            <span className="netiv-shell__icon-btn" aria-label="Profile">
              <UserCircle size={15} />
            </span>
          </div>
        </div>
        {ticker && ticker.length > 0 && (
          <div className="netiv-shell__ticker">
            {ticker.map((t, i) => (
              <div className="netiv-ticker-item" key={i}>
                <span className="netiv-ticker-item__label">{t.label}</span>
                <span className="netiv-ticker-item__value">{t.value}</span>
                {t.delta && (
                  <span className={tickerToneClass[t.direction ?? "neutral"]}>{t.delta}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </header>

      {roles && roles.length > 0 && (
        <div className="netiv-shell__roles" role="tablist" aria-label="Role">
          {roles.map((r) => {
            const Icon = r.icon;
            const on = r.id === activeRoleId;
            return (
              <button
                key={r.id}
                role="tab"
                aria-selected={on}
                className={`netiv-role-pill ${on ? "netiv-role-pill--on" : ""}`}
                onClick={() => onRoleChange?.(r.id)}
              >
                <Icon size={14} />
                {r.label}
              </button>
            );
          })}
        </div>
      )}

      <div className="netiv-shell__body">
        <nav className="netiv-shell__sidebar" aria-label="Sections">
          {navItems.map((n) => {
            const Icon = n.icon;
            const children = n.children ?? [];
            const groupOn = children.some((child) => child.id === activeNavId);

            if (children.length > 0) {
              return (
                <details className="netiv-sidebar-group" key={n.id} open>
                  <summary className={`netiv-sidebar-group__summary ${groupOn ? "netiv-sidebar-group__summary--on" : ""}`}>
                    <Icon size={17} strokeWidth={groupOn ? 2.25 : 1.75} />
                    <span>{n.label}</span>
                    <ChevronDown className="netiv-sidebar-group__chevron" size={15} />
                  </summary>
                  <div className="netiv-sidebar-group__items">
                    {children.map((child) => {
                      const ChildIcon = child.icon;
                      const on = child.id === activeNavId;
                      return (
                        <button
                          key={child.id}
                          className={`netiv-sidebar-item netiv-sidebar-item--child ${on ? "netiv-sidebar-item--on" : ""}`}
                          onClick={() => onNavChange(child.id)}
                          aria-current={on ? "page" : undefined}
                        >
                          <ChildIcon size={15} strokeWidth={on ? 2.25 : 1.75} />
                          {child.label}
                        </button>
                      );
                    })}
                  </div>
                </details>
              );
            }

            const on = n.id === activeNavId;
            return (
              <button
                key={n.id}
                className={`netiv-sidebar-item ${on ? "netiv-sidebar-item--on" : ""}`}
                onClick={() => onNavChange(n.id)}
                aria-current={on ? "page" : undefined}
              >
                <Icon size={17} strokeWidth={on ? 2.25 : 1.75} />
                {n.label}
              </button>
            );
          })}
        </nav>

        <main className="netiv-shell__content">
          {activeGroup && (
            <nav className="netiv-shell__subnav" aria-label={activeGroup.label}>
              {activeGroup.children?.map((child) => (
                <button
                  key={child.id}
                  className={`netiv-subnav-item ${child.id === activeNavId ? "netiv-subnav-item--on" : ""}`}
                  onClick={() => onNavChange(child.id)}
                  aria-current={child.id === activeNavId ? "page" : undefined}
                >
                  {child.label}
                </button>
              ))}
            </nav>
          )}
          {children}
        </main>
      </div>

      <nav className="netiv-shell__bottomnav" aria-label="Sections">
        <div className={`netiv-bottomnav-row ${navItems.length > 5 ? "netiv-bottomnav-row--scroll" : "netiv-bottomnav-row--even"}`}>
          {navItems.map((n) => {
            const Icon = n.icon;
            const children = n.children ?? [];
            const on = n.id === activeNavId || children.some((child) => child.id === activeNavId);
            const destination = children[0]?.id ?? n.id;
            return (
              <button
                key={n.id}
                className={`netiv-bottomnav-item ${on ? "netiv-bottomnav-item--on" : ""}`}
                onClick={() => onNavChange(destination)}
                aria-current={on ? "page" : undefined}
              >
                <Icon size={20} strokeWidth={on ? 2.25 : 1.75} />
                <span className="netiv-bottomnav-item__label">{n.mobileLabel ?? n.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
